import React from "react";
import { Link } from "react-router-dom";
import { Compass, BookOpen, Briefcase, Users, Sparkles, GraduationCap, CheckCircle2 } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const publics = [
  "Découvrir plusieurs métiers et environnements professionnels",
  "Donner du sens à leurs apprentissages",
  "Être accompagnés dans leur projet d'orientation",
  "Se préparer à une poursuite d'études en voie professionnelle, en CAP ou en bac professionnel",
];

const objectifs = [
  { icon: BookOpen, t: "Savoirs fondamentaux", d: "Renforcer le français, les mathématiques et les langues." },
  { icon: Briefcase, t: "Monde professionnel", d: "Découvrir les métiers, leurs réalités et leurs exigences." },
  { icon: Compass, t: "Projet d'orientation", d: "Construire un projet réaliste et motivé, étape par étape." },
  { icon: Sparkles, t: "Autonomie & confiance", d: "Grandir, gagner en maturité et oser ses choix." },
  { icon: GraduationCap, t: "Préparer le DNB", d: "Préparer sereinement le Diplôme National du Brevet." },
];

const formation = [
  "Séances de découverte des métiers et des formations",
  "Projets concrets et ateliers pratiques",
  "Stages en entreprise pour découvrir le monde du travail",
  "Visites d'établissements professionnels et de centres de formation",
  "Rencontres avec des professionnels de différents secteurs",
];

const stages = [
  "Découvrir le fonctionnement d'une entreprise",
  "Observer des métiers en situation réelle",
  "Confirmer ou ajuster son projet d'orientation",
  "Gagner en maturité et en autonomie",
];

const apres = [
  "Un CAP dans le domaine de leur choix",
  "Un baccalauréat professionnel",
  "Une formation en apprentissage",
];

export default function PrepaMetiers() {
  return (
    <div data-testid="prepa-metiers-page">
      <PageHero
        eyebrow="3ème Prépa-Métiers"
        title="Une année pour construire son avenir"
        subtitle="La classe de 3ème Prépa-Métiers s'adresse aux élèves souhaitant découvrir différents secteurs professionnels tout en poursuivant les apprentissages du collège. Une étape pour mûrir un projet d'orientation, gagner en confiance et préparer sereinement son entrée en voie professionnelle ou en apprentissage."
        image={IMAGES.workshop}
      />

      <Section eyebrow="À qui s'adresse cette classe" title="Une orientation choisie, pas subie">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div>
            <p className="text-base leading-relaxed text-muted-foreground">
              Cette formation est destinée aux élèves de 4ème qui souhaitent :
            </p>
            <ul className="mt-6 grid gap-3">
              {publics.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-2xl border border-black/5 bg-brand-cream p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-sage-dark" />
                  <span className="text-sm font-medium text-brand-slate-dark">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Élève en 3ème Prépa-Métiers" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40" eyebrow="Objectifs" title="Ce que l'on développe pendant l'année">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {objectifs.map((o, i) => (
            <Card key={o.t} data-testid={`prepa-objectif-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <o.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{o.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{o.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="Une formation concrète et motivante" title="Apprendre autrement, par l'expérience">
        <ul className="grid gap-3 sm:grid-cols-2">
          {formation.map((f) => (
            <li key={f} className="flex items-start gap-3 rounded-2xl bg-brand-sand-wrapper/50 p-5">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-brand-sand-dark" />
              <span className="text-sm font-medium text-brand-slate-dark">{f}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section wrapper="bg-brand-slate text-brand-cream" eyebrow="Les stages en entreprise" title="Des temps forts qui marquent l'année">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-base leading-relaxed text-brand-cream/85">
              Les périodes de stage sont essentielles dans le parcours de 3ème Prépa-Métiers. Elles
              permettent à chacun de confronter ses envies à la réalité du terrain et d'affiner ses
              choix d'orientation.
            </p>
            <ul className="mt-6 space-y-3">
              {stages.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm">
                  <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  <span className="text-brand-cream/90">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.classroom} alt="Stage en entreprise" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section eyebrow="Un accompagnement personnalisé" title="Chaque élève suivi pas à pas">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <Users className="h-7 w-7 text-brand-sage-dark" />
            <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
              Une équipe pédagogique attentive
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Identifier ses points forts, construire son projet d'orientation, préparer ses choix
              après la 3ème, réussir son parcours scolaire : l'équipe accompagne chaque élève en
              fonction de son rythme et de ses besoins.
            </p>
          </Card>
          <Card>
            <Compass className="h-7 w-7 text-brand-sage-dark" />
            <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
              Et après ?
            </h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              {apres.map((a) => (
                <li key={a} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                  {a}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/50">
        <div className="rounded-3xl bg-brand-cream p-10 text-center lg:p-16">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
            Rejoindre la 3ème Prépa-Métiers, c'est faire le choix d'une année tournée vers l'avenir
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground">
            Une passerelle concrète pour donner du sens à sa scolarité, découvrir des métiers et
            construire un projet d'avenir solide.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact?type=inscription"
              data-testid="prepa-cta-inscription"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              Demander un dossier d'inscription
            </Link>
            <Link
              to="/contact"
              data-testid="prepa-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
            >
              Poser une question
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
